import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OperationalSummaryCards } from './OperationalSummaryCards';

const innerTabsListClass = 'flex h-auto w-full flex-wrap justify-start gap-4 rounded-none border-b border-slate-200 bg-transparent px-3.5 py-0';
const innerTabsTriggerClass = 'h-9 rounded-none border-b-2 border-transparent px-0 text-xs font-medium uppercase tracking-[0.12em] text-slate-500 shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:text-slate-900';

export const OperationalDashboardTab = ({ dashboard, catalogSummary }) => {
  const rankingRows = dashboard?.ranking || [];
  const bottleneckRows = dashboard?.bottlenecks || [];
  const hotspotRows = dashboard?.activity_hotspots || [];
  const justificationRows = dashboard?.justification_breakdown || [];
  const recurrenceRows = catalogSummary?.recurrences || [];
  const areaRows = catalogSummary?.areas || [];

  return (
    <div className="space-y-2" data-testid="operational-dashboard-tab">
      <OperationalSummaryCards summary={dashboard?.summary} testIdPrefix="operational-dashboard-summary" />

      <Tabs defaultValue="compliance" className="space-y-2" data-testid="operational-dashboard-subtabs">
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="operational-dashboard-subtabs-shell">
          <TabsList className={innerTabsListClass} data-testid="operational-dashboard-subtabs-list">
            <TabsTrigger value="compliance" className={innerTabsTriggerClass} data-testid="operational-dashboard-subtab-compliance-trigger">Cumprimento</TabsTrigger>
            <TabsTrigger value="pending" className={innerTabsTriggerClass} data-testid="operational-dashboard-subtab-pending-trigger">Pendências</TabsTrigger>
            <TabsTrigger value="dynamism" className={innerTabsTriggerClass} data-testid="operational-dashboard-subtab-dynamism-trigger">Dinamismo</TabsTrigger>
            <TabsTrigger value="recurrences" className={innerTabsTriggerClass} data-testid="operational-dashboard-subtab-recurrences-trigger">Recorrências</TabsTrigger>
            <TabsTrigger value="areas" className={innerTabsTriggerClass} data-testid="operational-dashboard-subtab-areas-trigger">Áreas/Setores</TabsTrigger>
            <TabsTrigger value="collaborators" className={innerTabsTriggerClass} data-testid="operational-dashboard-subtab-collaborators-trigger">Colaboradores</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="compliance" className="mt-0" data-testid="operational-dashboard-subtab-compliance-content">
          <div className="grid gap-2.5 xl:grid-cols-[minmax(0,1.3fr)_360px]">
            <Card className="rounded-lg border-slate-200 bg-white shadow-sm" data-testid="operational-dashboard-ranking-card">
              <CardHeader className="px-3.5 pb-2 pt-3"><CardTitle className="text-base text-slate-900">Ranking operacional</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 px-3.5 pb-3.5 pt-0">
                {rankingRows.length === 0 ? <p data-testid="operational-dashboard-ranking-empty">Sem ranking disponível.</p> : rankingRows.map((item, index) => (
                  <div key={item.worker_id || index} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid={`operational-dashboard-ranking-row-${index}`}>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{item.worker_name}</p>
                      <p className="text-[11px] text-slate-500">Pendentes: {item.pending} · backlog: {item.backlog_index}</p>
                    </div>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{item.operational_score}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-lg border-slate-200 bg-white shadow-sm" data-testid="operational-dashboard-justifications-card">
              <CardHeader className="px-3.5 pb-2 pt-3"><CardTitle className="text-base text-slate-900">Justificações recorrentes</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 px-3.5 pb-3.5 pt-0">
                {justificationRows.length === 0 ? <p data-testid="operational-dashboard-justifications-empty">Sem justificações agregadas.</p> : justificationRows.map((item, index) => (
                  <div key={`${item.label}-${index}`} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`operational-dashboard-justification-row-${index}`}>
                    <p className="text-sm font-medium text-slate-900">{item.label}</p>
                    <p className="text-[11px] text-slate-500">{item.count} ocorrência(s)</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pending" className="mt-0" data-testid="operational-dashboard-subtab-pending-content">
          <div className="grid gap-2.5 xl:grid-cols-2">
            <Card className="rounded-lg border-slate-200 bg-white shadow-sm" data-testid="operational-dashboard-bottlenecks-card">
              <CardHeader className="px-3.5 pb-2 pt-3"><CardTitle className="text-base text-slate-900">Gargalos por área</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 px-3.5 pb-3.5 pt-0">
                {bottleneckRows.length === 0 ? <p data-testid="operational-dashboard-bottlenecks-empty">Sem gargalos detectados.</p> : bottleneckRows.map((item, index) => (
                  <div key={`${item.area}-${index}`} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`operational-dashboard-bottleneck-row-${index}`}>
                    <p className="text-sm font-medium text-slate-900">{item.area}</p>
                    <p className="text-[11px] text-slate-500">{item.count} pendência(s) abertas</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-lg border-slate-200 bg-white shadow-sm" data-testid="operational-dashboard-pending-hotspots-card">
              <CardHeader className="px-3.5 pb-2 pt-3"><CardTitle className="text-base text-slate-900">Hotspots de pendência</CardTitle></CardHeader>
              <CardContent className="grid gap-2 px-3.5 pb-3.5 pt-0 md:grid-cols-2">
                {hotspotRows.length === 0 ? <p data-testid="operational-dashboard-hotspots-empty">Sem hotspots detectados.</p> : hotspotRows.map((item, index) => (
                  <div key={`${item.title}-${index}`} className="rounded-md border border-slate-200 px-3 py-2.5" data-testid={`operational-dashboard-hotspot-row-${index}`}>
                    <p className="text-sm font-medium text-slate-900">{item.title}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{item.count} reincidência(s) abertas</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="dynamism" className="mt-0" data-testid="operational-dashboard-subtab-dynamism-content">
          <div className="grid gap-2.5 xl:grid-cols-[minmax(0,1.35fr)_320px]">
            <Card className="rounded-lg border-slate-200 bg-white shadow-sm" data-testid="operational-dashboard-dynamism-card">
              <CardHeader className="px-3.5 pb-2 pt-3"><CardTitle className="text-base text-slate-900">Actividades com maior reincidência</CardTitle></CardHeader>
              <CardContent className="grid gap-2 px-3.5 pb-3.5 pt-0 md:grid-cols-2 xl:grid-cols-3">
                {hotspotRows.length === 0 ? <p data-testid="operational-dashboard-dynamism-empty">Sem hotspots detectados.</p> : hotspotRows.map((item, index) => (
                  <div key={`${item.title}-${index}`} className="rounded-md border border-slate-200 px-3 py-2.5" data-testid={`operational-dashboard-dynamism-row-${index}`}>
                    <p className="text-sm font-medium text-slate-900">{item.title}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{item.count} reincidência(s) activas</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-lg border-slate-200 bg-white shadow-sm" data-testid="operational-dashboard-dynamism-summary-card">
              <CardHeader className="px-3.5 pb-2 pt-3"><CardTitle className="text-base text-slate-900">Leitura do dinamismo</CardTitle></CardHeader>
              <CardContent className="space-y-2 px-3.5 pb-3.5 pt-0">
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Hotspots</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{hotspotRows.length}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Justificações</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{justificationRows.reduce((total, item) => total + Number(item.count || 0), 0)}</p>
                </div>
                <p className="text-xs leading-5 text-slate-500">Esta leitura concentra sinais de repetição e fricção operacional sem espalhar métricas por vários blocos.</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="recurrences" className="mt-0" data-testid="operational-dashboard-subtab-recurrences-content">
          <div className="grid gap-2.5 xl:grid-cols-4">
            {recurrenceRows.length === 0 ? <p className="text-sm text-slate-500" data-testid="operational-dashboard-recurrences-empty">Sem recorrências agregadas.</p> : recurrenceRows.map((item) => (
              <Card key={item.key} className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={`operational-dashboard-recurrence-card-${item.key}`}>
                <CardContent className="px-3.5 py-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{item.key}</p>
                  <p className="mt-1 text-2xl font-semibold text-slate-950">{item.count}</p>
                  <p className="mt-1 text-[11px] text-slate-500">Itens do catálogo nesta recorrência.</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="areas" className="mt-0" data-testid="operational-dashboard-subtab-areas-content">
          <div className="grid gap-2.5 xl:grid-cols-[360px_minmax(0,1fr)]">
            <Card className="rounded-lg border-slate-200 bg-white shadow-sm" data-testid="operational-dashboard-areas-list-card">
              <CardHeader className="px-3.5 pb-2 pt-3"><CardTitle className="text-base text-slate-900">Áreas / setores</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 px-3.5 pb-3.5 pt-0">
                {areaRows.length === 0 ? <p data-testid="operational-dashboard-areas-empty">Sem áreas agregadas.</p> : areaRows.slice(0, 10).map((item) => (
                  <div key={item.key} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`operational-dashboard-area-row-${item.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900">{item.area}</p>
                      <Badge variant="outline" className="border-slate-200 bg-white text-slate-700">{item.count}</Badge>
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">{item.sector}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-lg border-slate-200 bg-white shadow-sm" data-testid="operational-dashboard-area-bottlenecks-card">
              <CardHeader className="px-3.5 pb-2 pt-3"><CardTitle className="text-base text-slate-900">Pressão por área</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 px-3.5 pb-3.5 pt-0">
                {bottleneckRows.length === 0 ? <p data-testid="operational-dashboard-area-bottlenecks-empty">Sem gargalos detectados.</p> : bottleneckRows.map((item, index) => (
                  <div key={`${item.area}-${index}`} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`operational-dashboard-area-bottleneck-${index}`}>
                    <p className="text-sm font-medium text-slate-900">{item.area}</p>
                    <p className="text-[11px] text-slate-500">{item.count} pendência(s) abertas</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="collaborators" className="mt-0" data-testid="operational-dashboard-subtab-collaborators-content">
          <Card className="rounded-lg border-slate-200 bg-white shadow-sm" data-testid="operational-dashboard-collaborators-card">
            <CardHeader className="px-3.5 pb-2 pt-3"><CardTitle className="text-base text-slate-900">Colaboradores</CardTitle></CardHeader>
            <CardContent className="space-y-1.5 px-3.5 pb-3.5 pt-0">
              {rankingRows.length === 0 ? <p data-testid="operational-dashboard-collaborators-empty">Sem dados de colaboradores.</p> : rankingRows.map((item, index) => (
                <div key={item.worker_id || index} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid={`operational-dashboard-collaborator-row-${index}`}>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{item.worker_name}</p>
                    <p className="text-[11px] text-slate-500">Pendentes: {item.pending} · backlog: {item.backlog_index}</p>
                  </div>
                  <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">{item.operational_score}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};